<template>
    <IonLabel class="hint">
        Die Summe der Umsätze aller Fachkräfte bei <Ines/> abzüglich sämtlicher Aufwendungen des laufenden Jahres ergeben den
        <DetailsPopover label="kumulierten Jahresüberschuss" text-only inline side="bottom">
            <!--<p class="globalJustifyHyphenate">Monatlich laufen die <i>Überschussbeiträge</i> aller Vereinsmitglieder zusammen und werden &ndash; um die Allgemeinkosten reduziert &ndash; als Monatsüberschuss des Vereins transparent gemacht. Die einzelnen Monatsüberschüsse werden über das Jahr aufsummiert und münden im Jahresabschluss. </p>
            <p class="globalJustifyHyphenate">Jedes Vereinsmitglied wird anteilig an der Überschussausschüttung beteiligt. Als Schlüssel werden die individuellen Überschussbeiträge verwendet. Der exakte Betrag steht erst zum Jahresabschluss fest.</p>-->
            <p class="globalJustifyHyphenate">Regelmäßige Schwankungen führen dazu, dass nicht jeder einzelne Monate mit einem positiven Ergebnis endet. Diese monatlichen Schwankungen werden über das Jahr aufsummiert und dadurch ausgeglichen.</p>
            <p class="globalJustifyHyphenate">Mit Ablauf eines Kalenderjahres wird die Differenz aus Umsatz und Aufwand bestimmt. Die positive Differenz (Jahresüberschuss) steht zur Ausschüttung zur Verfügung. Diese Feststellung erfolgt im Mai des Folgejahres. Der aktuelle Jahresüberschuss wird monatlich näherungsweise dargestellt.</p>
        </DetailsPopover> und deinen
        <DetailsPopover label="individuellen Anteil" text-only inline side="bottom">
            <p class="globalJustifyHyphenate">Dein individueller Anteil am Jahresüberschuss orientiert sich an der Produktivität. Deine kumulierten Beiträge zum Jahresüberschuss gelten als Verteilungsschlüssel.</p>
        </DetailsPopover>
        daran.
    </IonLabel>
    <Skeleton v-if="initialDataLoader.pending.value" :items="[`${APEX_SKELETON_WITH_SUMMARY}`, 'block']"/>
    <template v-else>
        <div class="boxed margin-bottom-regular">
            <ApexChartValueLine :values="[
                {
                    label: monthRanger.selectedCalendarMonth.value.format(),
                    value: monthRanger.selectedRenderSet.value.excessMonthly,
                    formatter: value => money.formatMoney(value as Money, {signDisplay:'exceptZero'})
                },
                {
                    label: 'Gesamtüberschuss',
                    value: monthRanger.selectedRenderSet.value.excessCumulative,
                    formatter: value => money.formatMoney(value as Money,    {signDisplay:'negative'}) + ' kum.'
                }
            ]"/>
            <ApexChartWrapper :options="apexOptions" />
        </div>
        <ApexChartSummaryLine
            :number="isSet(ytdMonthRenderSet.myExcessShare) ? money.formatLargeMoney(ytdMonthRenderSet.myExcessShare) : '-'"
            :text="`individueller Anteil am Jahresüberschuss ${ytdMonthRenderSet.month ? date.formatKapitelDate(ytdMonthRenderSet.month, 'y') : '-'} zum ${ytdMonthRenderSet.month ? date.formatKapitelDate(date.endOfMonth(ytdMonthRenderSet.month), 'd. MMMM') : ''}.` /*+ (
                money.gt(initialMonthRenderSet.myExcessShareHourly, money.Zero)
                ? ` Umgerechnet auf den Studenlohn: ${money.formatMoney(initialMonthRenderSet.myExcessShareHourly, {signDisplay: 'exceptZero'})}/h`
                : ''
            )*/"
        />
        
    </template>
</template>

<script setup lang="ts">
import {computed, ComputedRef, PropType, Ref, ref, watch} from 'vue';
import {CalendarMonth} from "@/helper/calendar/calendarMonth";
import {DataLoader} from "@/helper/dataLoader";

import Skeleton from "@/components/Skeleton.vue";
import {IbfgMonthlyFinancialResultSummary, IbfgYearlyFinancialResultSummary, Money} from "@/graphql/generated/graphql";

import {kapitelMonthString} from '@/graphql/kapitelTypes';
import {toMap} from "@/helper/array";
import * as money from "@/helper/money";
import {APEX_SKELETON_WITH_SUMMARY, generateApexOptions} from "@/helper/apexchart";
import month from "@/helper/datetime/month";
import ApexChartWrapper from "@/views/components/ApexChartWrapper.vue";
import ApexChartValueLine from "@/views/components/ApexChartValueLine.vue";
import {IonLabel} from "@ionic/vue";
import date from "@/helper/datetime/date";
import {fetchIbfgFinancialResultToDate, monthlyResultsSorter} from "@/helper/monthKPI/monthKpiIBFG";
import ApexChartSummaryLine from "@/views/components/ApexChartSummaryLine.vue";
import DetailsPopover from "@/views/components/DetailsPopover.vue";
import {isSet, isUnset, nullish} from "@/helper/isset";
import Ines from "@/views/components/Ines.vue";
import {MonthKPIApexMonthRangerFactory} from "@/helper/monthKPI/monthKPI";

// props
const props = defineProps({
    selectedMonth: {
        type: String as PropType<kapitelMonthString>,
        required: true,
    }
});

// loader
const initialDataLoader = new DataLoader();

/**
 * month range & selection
 */
const monthRanger = MonthKPIApexMonthRangerFactory({
    selected: props.selectedMonth,
    renderer: (calendarMonth: CalendarMonth) => {
        const monthlyResult = ibfgMonthlyResultMap.value.get(calendarMonth.getId());
        const cumulativeResult = ibfgMonthlyResultCumulativeMap.value.get(calendarMonth.getId())
        return {
            excessMonthly: monthlyResult?.result ?? null,
            excessCumulative: cumulativeResult ?? null,
            year: month.getKapitelYearStringFromKapitelMonthString(calendarMonth.getId())
        }
    }
})

const apexOptions = computed(() => {
    const renderSets = monthRanger.allMonthsRenderSets.map(rs => rs.value)
    const years = [...new Set(renderSets.map(rs => rs.year))]

    const series = years.map((year) => ({
        name: `Überschuss ${year}`,
        data: renderSets.map((rs) => rs.year === year && isSet(rs.excessCumulative) ? money.toFloat(rs.excessCumulative) : null),
    }))

    return generateApexOptions(
        monthRanger,
        {
            series,
            chart: { type:'area' },
            yaxis: {
                labels: {
                    formatter: (value: number|nullish) => isUnset(value) ? '-' : money.formatLargeFloat(value, {notation:"compact", compactDisplay: "short"}, 'en-US'),
                    // ensure equal width across all monthKPI charts
                    maxWidth: 50,
                    minWidth: 50,
                }
            }
        },
        (index: number) => {
            const value = renderSets[index]?.excessCumulative
            return isSet(value)
                ? money.formatLargeFloat(money.toFloat(value), {notation:"compact", compactDisplay: "short"}, 'en-US')
                : '-'
        }
    )

})


/**
 * raw data
 */
const ibfgSummaryToInitialDate : Ref<IbfgYearlyFinancialResultSummary|undefined> = ref(undefined)
initialDataLoader.add(
    async () => ibfgSummaryToInitialDate.value = await fetchIbfgFinancialResultToDate(props.selectedMonth)
)



const ibfgSummaryToMaxDate : Ref<IbfgYearlyFinancialResultSummary|undefined> = ref(undefined)
initialDataLoader.add(
    async () => ibfgSummaryToMaxDate.value = props.selectedMonth === monthRanger.max ? undefined : await fetchIbfgFinancialResultToDate(monthRanger.max)
)
const previousYearMaxMonthForChart = (() => {
    const minYear = month.getKapitelYearStringFromKapitelMonthString(monthRanger.min)
    const maxYear = month.getKapitelYearStringFromKapitelMonthString(monthRanger.max)
    return minYear === maxYear ? undefined : month.lastMonthOfYear(minYear)
})()
const ibfgSummaryPreviousYearToMaxDate : Ref<IbfgYearlyFinancialResultSummary|undefined> = ref(undefined)
initialDataLoader.add(async () => {
    ibfgSummaryPreviousYearToMaxDate.value = previousYearMaxMonthForChart ? await fetchIbfgFinancialResultToDate(previousYearMaxMonthForChart) : undefined
})

const ibfgMonthlyResults = computed(() => {
    const currentYearSummary = props.selectedMonth === monthRanger.max ? ibfgSummaryToInitialDate.value : ibfgSummaryToMaxDate.value
    const currentYearResults = currentYearSummary?.monthlyResults ?? []
    const previousYearResults = ibfgSummaryPreviousYearToMaxDate.value?.monthlyResults ?? []
    return [...previousYearResults, ...currentYearResults]
})
const ibfgMonthlyResultMap : ComputedRef<Map<kapitelMonthString, IbfgMonthlyFinancialResultSummary>> = computed(() => toMap(ibfgMonthlyResults.value, (a) => a.month as kapitelMonthString))
const ibfgMonthlyResultCumulativeMap : ComputedRef<Map<kapitelMonthString, Money>> = computed(() => {
    const mergedMap = new Map<kapitelMonthString, Money>()
    const cumsumPerYear = new Map<string, Money>()
    ibfgMonthlyResults.value
        .toSorted(monthlyResultsSorter)
        .map((e) => {
            const monthId = e.month as kapitelMonthString
            const year = month.getKapitelYearStringFromKapitelMonthString(monthId)
            const yearCumsum = cumsumPerYear.get(year) ?? money.Zero
            const nextCumsum = money.add(e.result, yearCumsum)
            cumsumPerYear.set(year, nextCumsum)
            mergedMap.set(monthId, nextCumsum)
        })
    return mergedMap
})




const selectedYTD = ref(undefined as IbfgYearlyFinancialResultSummary | undefined)
watch(() => monthRanger.selected.value, async (month: kapitelMonthString) => selectedYTD.value = await fetchIbfgFinancialResultToDate(month))

const ytdMonthRenderSet = computed(() => {
    const summary = selectedYTD.value ?? ibfgSummaryToInitialDate.value

    return {
        month: summary?.lastMonthIncluded,
        excessTotal: summary?.cumSumResults ?? null,
        myExcessShare: summary?.myExcessShare ?? null,
        myExcessShareHourly: summary?.myHourlyPlusByExcessShare ?? null
    }
})



/**
 * reset
 */

const reset = async () => initialDataLoader.reload()
defineExpose({reset})
reset()

</script>
